import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Plus, X, Edit3, Trash2, CheckCircle } from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';

const LanguePage = () => {
    const { user } = useGlobale();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [langues, setLangues] = useState([]);
    const [listeLangues, setListeLangues] = useState([]);
    const [niveauxLangue, setNiveauxLangue] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedLangue, setSelectedLangue] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const [formData, setFormData] = useState({
        langue: '',
        parler_niveau: '1',
        ecrire_niveau: '1',
        lire_niveau: '1'
    });

    // État pour suivre si le formulaire contient des erreurs
    const [errors, setErrors] = useState({});

    // Set sidebar initial state based on screen size
    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 1024);
        };

        // Set initial state
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchLangues();
        fetchListeLangues();
        fetchNiveauxLangue();
    }, []);

    const fetchLangues = async () => {
        setLoading(true);
        try {
            const languagesResponse = await api.get('demandeur/langues/');
            // Les langues sont déjà enrichies avec les détails grâce à LangueDetailSerializer
            setLangues(languagesResponse.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des langues:', error);
            toast.error('Erreur lors de la récupération de vos langues');
        } finally {
            setLoading(false);
        }
    };

    const fetchListeLangues = async () => {
        try {
            const response = await api.get('demandeur/liste-langues/');
            setListeLangues(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération de la liste des langues:', error);
            toast.error('Erreur lors de la récupération de la liste des langues');
        }
    };

    const fetchNiveauxLangue = async () => {
        try {
            const response = await api.get('demandeur/niveaux-langue/');
            setNiveauxLangue(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des niveaux de langue:', error);
            toast.error('Erreur lors de la récupération des niveaux de langue');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Effacer l'erreur lorsqu'un champ est modifié
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.langue) {
            newErrors.langue = "La langue est requise";
        }

        if (!formData.parler_niveau) {
            newErrors.parler_niveau = "Le niveau de parler est requis";
        }

        if (!formData.ecrire_niveau) {
            newErrors.ecrire_niveau = "Le niveau d'écriture est requis";
        }

        if (!formData.lire_niveau) {
            newErrors.lire_niveau = "Le niveau de lecture est requis";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            // Construire l'objet de données à envoyer avec conversion explicite
            const langueDonnees = {
                langue: formData.langue,
                parler_niveau: String(formData.parler_niveau), // Assurez-vous que c'est une chaîne
                ecrire_niveau: String(formData.ecrire_niveau), // Assurez-vous que c'est une chaîne
                lire_niveau: String(formData.lire_niveau),     // Assurez-vous que c'est une chaîne
                utilisateur: user.id
            };
            
            // console.log("Données à envoyer:", langueDonnees); // Debug
            
            if (isEditMode && selectedLangue) {
                await api.put(`demandeur/langues/${selectedLangue.id}/`, langueDonnees);
                toast.success('Langue mise à jour avec succès');
            } else {
                await api.post('demandeur/langues/', langueDonnees);
                toast.success('Langue ajoutée avec succès');
            }

            fetchLangues();
            setShowModal(false);
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de la langue:', error);
            
            if (error.response && error.response.data) {
                console.error("Détails de l'erreur:", error.response.data);
                
                if (typeof error.response.data === 'object') {
                    setErrors(error.response.data);
                } else {
                    toast.error('Erreur lors de la sauvegarde: ' + JSON.stringify(error.response.data));
                }
            } else {
                toast.error('Erreur de connexion au serveur');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            await api.delete(`demandeur/langues/${id}/`);
            toast.success('Langue supprimée avec succès');
            setDeleteConfirmation(null);
            // Mettre à jour la liste des langues
            fetchLangues();
        } catch (error) {
            console.error('Erreur lors de la suppression de la langue:', error);
            toast.error('Erreur lors de la suppression de la langue');
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (langue) => {
        setIsEditMode(true);
        setSelectedLangue(langue);
        
        // Récupérer les informations de niveau depuis les détails de niveau déjà présents
        if (langue.niveau_details) {
            setFormData({
                langue: langue.langue,
                parler_niveau: langue.niveau_details.parler_niveau,
                ecrire_niveau: langue.niveau_details.ecrire_niveau,
                lire_niveau: langue.niveau_details.lire_niveau
            });
        } else {
            // Fallback au cas où les détails ne sont pas disponibles
            api.get(`demandeur/niveaux-langue/${langue.niveau}/`)
                .then(response => {
                    const niveauLangue = response.data;
                    setFormData({
                        langue: langue.langue,
                        parler_niveau: niveauLangue.parler_niveau,
                        ecrire_niveau: niveauLangue.ecrire_niveau,
                        lire_niveau: niveauLangue.lire_niveau
                    });
                })
                .catch(error => {
                    // console.error('Erreur lors de la récupération du niveau:', error);
                    toast.error('Erreur lors de la récupération du niveau de langue');
                    // Si erreur, initialiser avec des valeurs par défaut
                    setFormData({
                        langue: langue.langue,
                        parler_niveau: '1',
                        ecrire_niveau: '1',
                        lire_niveau: '1'
                    });
                });
        }
        
        setShowModal(true);
    };

    const openAddModal = () => {
        setIsEditMode(false);
        setSelectedLangue(null);
        setFormData({
            langue: '',
            parler_niveau: '1',
            ecrire_niveau: '1',
            lire_niveau: '1'
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setErrors({});
    };

    // Fonction pour obtenir le nom de la langue
    const getLangueNom = (langueId) => {
        const langue = listeLangues.find(l => l.id === langueId);
        return langue ? langue.langue : 'Non spécifiée';
    };

    // Fonction pour obtenir les informations du niveau
    const getNiveauInfo = (niveauId, detailedNiveau = null) => {
        // Si nous avons les détails de niveau fournis directement
        if (detailedNiveau) {
            // Déterminer le niveau moyen
            const niveauMoyen = Math.floor((parseInt(detailedNiveau.parler_niveau) + 
                                            parseInt(detailedNiveau.ecrire_niveau) + 
                                            parseInt(detailedNiveau.lire_niveau)) / 3);

            let label, color;
            switch(niveauMoyen) {
                case 1:
                    label = 'Débutant';
                    color = 'bg-red-100 text-red-600';
                    break;
                case 2:
                    label = 'Intermédiaire';
                    color = 'bg-yellow-100 text-yellow-600';
                    break;
                case 3:
                    label = 'Avancé';
                    color = 'bg-green-100 text-green-600';
                    break;
                default:
                    label = 'Non spécifié';
                    color = 'bg-gray-100 text-gray-600';
            }

            return {
                label,
                color,
                parler: detailedNiveau.parler_niveau,
                ecrire: detailedNiveau.ecrire_niveau,
                lire: detailedNiveau.lire_niveau
            };
        }

        // Si nous n'avons pas encore les niveaux
        if (!niveauxLangue) {
            return { 
                label: 'Chargement...', 
                color: 'bg-gray-100 text-gray-600',
                parler: '?',
                ecrire: '?',
                lire: '?'
            };
        }

        try {
            // On essaie de trouver le niveau spécifique
            const niveau = niveauxLangue.find(n => n.id === niveauId);
            if (!niveau) {
                return { 
                    label: 'Non spécifié', 
                    color: 'bg-gray-100 text-gray-600',
                    parler: '-',
                    ecrire: '-',
                    lire: '-'
                };
            }

            // Déterminer le niveau moyen
            const niveauMoyen = Math.floor((parseInt(niveau.parler_niveau) + 
                                            parseInt(niveau.ecrire_niveau) + 
                                            parseInt(niveau.lire_niveau)) / 3);

            let label, color;
            switch(niveauMoyen) {
                case 1:
                    label = 'Débutant';
                    color = 'bg-red-100 text-red-600';
                    break;
                case 2:
                    label = 'Intermédiaire';
                    color = 'bg-yellow-100 text-yellow-600';
                    break;
                case 3:
                    label = 'Avancé';
                    color = 'bg-green-100 text-green-600';
                    break;
                default:
                    label = 'Non spécifié';
                    color = 'bg-gray-100 text-gray-600';
            }

            return {
                label,
                color,
                parler: niveau.parler_niveau,
                ecrire: niveau.ecrire_niveau,
                lire: niveau.lire_niveau
            };
        } catch (error) {
            console.error("Erreur lors de la récupération des informations de niveau:", error);
            return { 
                label: 'Erreur', 
                color: 'bg-gray-100 text-gray-600',
                parler: '-',
                ecrire: '-',
                lire: '-'
            };
        }
    };

    // Fonction pour obtenir une couleur de fond aléatoire
    const getRandomBackgroundColor = () => {
        const colors = [
            'bg-orange-50',
            'bg-amber-50',
            'bg-yellow-50',
            'bg-lime-50',
            'bg-emerald-50',
            'bg-teal-50',
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const renderProgressBar = (niveau) => {
        const niveauInfo = getNiveauInfo(niveau);
        let value = 0;
        
        switch(niveauInfo.label) {
            case 'Débutant':
                value = 33;
                break;
            case 'Intermédiaire':
                value = 66;
                break;
            case 'Avancé':
                value = 100;
                break;
            default:
                value = 0;
        }
        
        let colorClass = '';
        switch(niveauInfo.label) {
            case 'Débutant':
                colorClass = 'bg-red-500';
                break;
            case 'Intermédiaire':
                colorClass = 'bg-yellow-500';
                break;
            case 'Avancé':
                colorClass = 'bg-green-500';
                break;
            default:
                colorClass = 'bg-gray-500';
        }
        
        return (
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                    className={`${colorClass} h-2 rounded-full`} 
                    style={{ width: `${value}%` }}
                ></div>
            </div>
        );
    };

    const renderLangueCards = () => {
        if (langues.length === 0) {
            return (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                    <div className="mb-6">
                        <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 mx-auto">
                            <Globe size={36} strokeWidth={1.5} />
                        </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Aucune langue</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        Ajoutez vos compétences linguistiques pour mettre en valeur votre profil auprès des recruteurs internationaux.
                    </p>
                </div>
            );
        }

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {langues.map((langue) => {
                    const niveauInfo = getNiveauInfo(langue.niveau, langue.niveauInfo);
                    const bgColor = getRandomBackgroundColor();

                    return (
                        <div
                            key={langue.id}
                            className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition duration-300 transform hover:-translate-y-1 border border-gray-100"
                        >
                            <div className="p-6">
                                {/* En-tête avec nom de la langue et actions */}
                                <div className="flex justify-between items-start mb-5">
                                    <div className="flex items-center">
                                        <div className={`p-3 rounded-full mr-3 ${bgColor}`}>
                                            <Globe className="h-6 w-6 text-orange-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800">
                                                {langue.langue_nom || (langue.langueInfo ? langue.langueInfo.langue : getLangueNom(langue.langue))}
                                            </h3>
                                            <div className={`text-sm px-2 py-1 rounded-full inline-flex items-center mt-1 ${niveauInfo.color}`}>
                                                <CheckCircle size={12} className="mr-1" />
                                                {niveauInfo.label}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => openEditModal(langue)}
                                            className="bg-orange-50 hover:bg-orange-100 text-orange-600 p-2 rounded-full transition-colors duration-200"
                                            title="Modifier"
                                        >
                                            <Edit3 size={16} />
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirmation(langue.id)}
                                            className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-full transition-colors duration-200"
                                            title="Supprimer"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                {/* Niveaux détaillés avec visualisation */}
                                <div className="mt-4 space-y-3 bg-gray-50 p-3 rounded-lg">
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-sm font-medium text-gray-700">Parler</span>
                                            <span className="text-sm bg-gray-200 rounded-full px-2 py-0.5">
                                                Niveau {langue.niveau_details ? langue.niveau_details.parler_niveau : (langue.niveauInfo ? langue.niveauInfo.parler_niveau : niveauInfo.parler)}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-blue-500 h-2 rounded-full" 
                                                style={{ 
                                                    width: `${parseInt(langue.niveau_details ? langue.niveau_details.parler_niveau : (langue.niveauInfo ? langue.niveauInfo.parler_niveau : niveauInfo.parler)) * 33}%` 
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-sm font-medium text-gray-700">Écrire</span>
                                            <span className="text-sm bg-gray-200 rounded-full px-2 py-0.5">
                                                Niveau {langue.niveau_details ? langue.niveau_details.ecrire_niveau : (langue.niveauInfo ? langue.niveauInfo.ecrire_niveau : niveauInfo.ecrire)}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-green-500 h-2 rounded-full" 
                                                style={{ 
                                                    width: `${parseInt(langue.niveau_details ? langue.niveau_details.ecrire_niveau : (langue.niveauInfo ? langue.niveauInfo.ecrire_niveau : niveauInfo.ecrire)) * 33}%` 
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-sm font-medium text-gray-700">Lire</span>
                                            <span className="text-sm bg-gray-200 rounded-full px-2 py-0.5">
                                                Niveau {langue.niveau_details ? langue.niveau_details.lire_niveau : (langue.niveauInfo ? langue.niveauInfo.lire_niveau : niveauInfo.lire)}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-purple-500 h-2 rounded-full" 
                                                style={{ 
                                                    width: `${parseInt(langue.niveau_details ? langue.niveau_details.lire_niveau : (langue.niveauInfo ? langue.niveauInfo.lire_niveau : niveauInfo.lire)) * 33}%` 
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Niveau global */}
                                <div className="mt-4">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-sm font-medium text-gray-700">Niveau global</span>
                                    </div>
                                    {renderProgressBar(langue.niveau)}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderContent = () => {
        if (loading && langues.length === 0) {
            return (
                <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
                </div>
            );
        }

        return (
            <>
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-[#437011] mb-2">
                            Langues
                        </h1>
                        <p className="text-gray-600">Gérez vos compétences linguistiques</p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-orange-700 transition mt-4 md:mt-0 w-full md:w-auto justify-center"
                    >
                        <Plus size={18} className="mr-2" />
                        Ajouter une langue
                    </button>
                </div>

                {renderLangueCards()}
            </>
        );
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1 flex flex-col w-full">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {renderContent()}
                </main>
            </div>

            {/* Modal pour ajouter/éditer une langue */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {isEditMode ? "Modifier la langue" : "Ajouter une langue"}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Langue <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="langue"
                                        value={formData.langue}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.langue ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionnez une langue</option>
                                        {listeLangues.map(langue => (
                                            <option key={langue.id} value={langue.id}>
                                                {langue.langue}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.langue && <p className="mt-1 text-sm text-red-600">{errors.langue}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Niveau de parler <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="parler_niveau"
                                        value={formData.parler_niveau}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.parler_niveau ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionnez un niveau</option>
                                        <option value="1">Niveau 1 (Débutant)</option>
                                        <option value="2">Niveau 2 (Intermédiaire)</option>
                                        <option value="3">Niveau 3 (Avancé)</option>
                                    </select>
                                    {errors.parler_niveau && <p className="mt-1 text-sm text-red-600">{errors.parler_niveau}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Niveau d'écriture <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="ecrire_niveau"
                                        value={formData.ecrire_niveau}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.ecrire_niveau ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionnez un niveau</option>
                                        <option value="1">Niveau 1 (Débutant)</option>
                                        <option value="2">Niveau 2 (Intermédiaire)</option>
                                        <option value="3">Niveau 3 (Avancé)</option>
                                    </select>
                                    {errors.ecrire_niveau && <p className="mt-1 text-sm text-red-600">{errors.ecrire_niveau}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Niveau de lecture <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="lire_niveau"
                                        value={formData.lire_niveau}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.lire_niveau ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionnez un niveau</option>
                                        <option value="1">Niveau 1 (Débutant)</option>
                                        <option value="2">Niveau 2 (Intermédiaire)</option>
                                        <option value="3">Niveau 3 (Avancé)</option>
                                    </select>
                                    {errors.lire_niveau && <p className="mt-1 text-sm text-red-600">{errors.lire_niveau}</p>}
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`px-4 py-2 rounded-lg text-white ${loading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'}`}
                                >
                                    {loading ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Enregistrement...
                                        </span>
                                    ) : (
                                        isEditMode ? "Mettre à jour" : "Ajouter"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de confirmation de suppression */}
            {deleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirmer la suppression</h3>
                            <p className="text-gray-600 mb-6">
                                Êtes-vous sûr de vouloir supprimer cette langue ? Cette action est irréversible.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setDeleteConfirmation(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={() => handleDelete(deleteConfirmation)}
                                    className="px-4 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LanguePage;
